import html2canvas from 'html2canvas';

/**
 * Captura de tela do mapa, compartilhada entre os mapas Mapbox.
 *
 * MapBox desenha em canvas WebGL, então usamos `map.getCanvas().toDataURL()` — o que
 * exige `preserveDrawingBuffer: true` na criação do mapa. Apenas o conteúdo do mapa
 * (basemap + marcadores + camadas) é capturado: controles e barras laterais são DOM
 * sobreposto e ficam de fora.
 *
 * Contrato do hospedeiro: `self.map`, `self.setState`, `self.restoreDragPan()` e o
 * estado `show_map_capture` / `mapCaptureData` / `mapCaptureCanvas`.
 */

export const MAP_CAPTURE_INITIAL_STATE = {
    show_map_capture: false,
    mapCaptureData: null,
    mapCaptureCanvas: null,
};

export async function printMap(self) {
    if (!self.map) return;

    // Garante que o último frame foi desenhado antes de ler os pixels.
    await new Promise((resolve) => {
        self.map.once('idle', resolve);
        self.map.triggerRepaint();
    });

    const canvas = await composeCaptureWithLegend(self.map.getCanvas());
    self.setState({
        mapCaptureCanvas: canvas,
        mapCaptureData: canvas.toDataURL('image/jpeg', 0.95),
        show_map_capture: true,
    });
}

/**
 * A legenda flutuante dos polos é DOM sobreposto, então o canvas WebGL não a contém —
 * mas ela precisa sair no relatório, como no mapa Google (printMap do DefaulMap empurra a
 * legenda para a borda antes de clonar). Rasteriza a legenda e a desenha por cima de uma
 * cópia 2D do canvas do mapa, encostada no canto inferior esquerdo: na tela ela fica
 * deslocada para a direita pela barra lateral, que não entra na captura, e naquela posição
 * cobriria o conteúdo do mapa (raio de deslocamento, marcadores).
 *
 * @param {HTMLCanvasElement} mapCanvas Canvas WebGL do MapBox.
 * @returns {Promise<HTMLCanvasElement>} O próprio canvas do mapa, se não houver legenda.
 */
export async function composeCaptureWithLegend(mapCanvas) {
    const legendaEl = document.querySelector('.mapbox-polos-legend');
    if (!legendaEl) return mapCanvas;

    try {
        const mapRect = mapCanvas.getBoundingClientRect();
        const escala = mapCanvas.width / mapRect.width;

        const legendaCanvas = await html2canvas(legendaEl, { backgroundColor: null, scale: escala });

        const composto = document.createElement('canvas');
        composto.width = mapCanvas.width;
        composto.height = mapCanvas.height;

        const ctx = composto.getContext('2d');
        ctx.drawImage(mapCanvas, 0, 0);

        // Mesmas margens do CSS da legenda (left 12px, bottom 30px), em pixels do canvas.
        const margemX = 12 * escala;
        const margemY = 30 * escala;
        ctx.drawImage(
            legendaCanvas,
            margemX,
            Math.max(0, composto.height - legendaCanvas.height - margemY)
        );

        return composto;
    } catch (e) {
        console.error('[mapCapture] erro ao compor a legenda na captura:', e);
        return mapCanvas;
    }
}

export function closeMapCapture(self) {
    self.restoreDragPan();
    self.setState({ show_map_capture: false });
}
